/**
 * API client for the WB Contests surfaces.
 *
 * Every endpoint is gated server-side by `homev2:read`; hiding a control here is not
 * authorization. Requests take an `AbortSignal`, so a filter change cancels work
 * already in flight — `docs/contests/ARCHITECTURE.md` §4 requires stale responses to be
 * discarded, and
 * React Query only does that reliably when the fetch is abortable.
 *
 * Shaped after `leaderboards-service.ts` rather than diverging from it: same auth
 * header, same `{code, detail}` error handling, same guarded JSON parse for the case
 * where a proxy returns HTML.
 */

import type {
  ContestErrorCode,
  ContestSummary,
  EditableContest,
  EditorOptions,
  FlyerResponse,
  PersonOption,
  ProfileResponse,
  ProofResponse,
  ShowcaseResponse,
  StandingsQuery,
  StandingsResponse,
  ThresholdMetric,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const BASE = '/api/wbreporting';

/** An API failure carrying the backend's stable error code, when it sent one. */
export class ContestError extends Error {
  readonly code?: ContestErrorCode;
  readonly status: number;

  constructor(message: string, status: number, code?: ContestErrorCode) {
    super(message);
    this.name = 'ContestError';
    this.status = status;
    this.code = code;
  }
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('wb.authToken');
  return {
    Authorization: `Token ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Turn a non-2xx response into a `ContestError`.
 *
 * The backend answers 4xx with `{code, detail}`. A gateway or proxy might still
 * produce HTML, so the JSON parse is guarded rather than assumed.
 */
async function describeFailure(response: Response): Promise<ContestError> {
  try {
    const body = await response.json();
    return new ContestError(
      typeof body?.detail === 'string' ? body.detail : `Request failed: ${response.status}`,
      response.status,
      body?.code
    );
  } catch {
    return new ContestError(`Request failed: ${response.status}`, response.status);
  }
}

async function getJson<T>(path: string, params: URLSearchParams, signal?: AbortSignal): Promise<T> {
  const query = params.toString();
  const response = await fetch(`${API_BASE_URL}${BASE}${path}${query ? `?${query}` : ''}`, {
    headers: getAuthHeaders(),
    signal,
  });
  if (!response.ok) throw await describeFailure(response);
  return (await response.json()) as T;
}

/** Readable contests for the card's selector, active ones first. */
export async function fetchContests(signal?: AbortSignal): Promise<ContestSummary[]> {
  const body = await getJson<{ results: ContestSummary[] }>(
    '/contest-board/',
    new URLSearchParams(),
    signal
  );
  return body.results;
}

/** dtez's person label, `Name [CODE]`, which the search box shows and matches on. */
export function personLabel(name: string, agencyCode: string): string {
  const shown = name.trim() || agencyCode;
  return agencyCode ? `${shown} [${agencyCode}]` : shown;
}

/**
 * Coded people matching a partial name or agent code, for the person search.
 *
 * The same `accounts/users/` search the shared `UserAutocompleteDropdown` uses, coded
 * users only: contest scopes hold nobody else (C22).
 */
export async function searchPeople(term: string, signal?: AbortSignal): Promise<PersonOption[]> {
  const params = new URLSearchParams({ has_agency_code: 'true', page_size: '10', search: term });
  const response = await fetch(`${API_BASE_URL}/api/accounts/users/?${params}`, {
    headers: getAuthHeaders(),
    signal,
  });
  if (!response.ok) throw await describeFailure(response);
  const body = (await response.json()) as {
    results: Array<{
      id: number;
      full_name?: string;
      first_name?: string;
      last_name?: string;
      agency_code?: string;
    }>;
  };
  return body.results.map((user) => {
    const name = user.full_name || `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
    const agencyCode = user.agency_code ?? '';
    return { id: user.id, name, agencyCode, label: personLabel(name, agencyCode) };
  });
}

/** Query parameters for one standings request. */
export function standingsParams(query: StandingsQuery): URLSearchParams {
  const params = new URLSearchParams({
    scope: query.scope,
    net: String(query.net),
    leaders: String(query.leaders),
    agents: String(query.agents),
    direction: query.direction,
  });
  if (query.personId !== null && query.scope !== 'all') {
    params.set('person', String(query.personId));
  }
  if (query.tierIds.length) params.set('tiers', query.tierIds.join(','));
  if (query.sortTier !== null) params.set('sort_tier', String(query.sortTier));
  if (query.cursor) params.set('cursor', query.cursor);
  return params;
}

/**
 * One prepared page of standings.
 *
 * The response contains evaluations, not result rows: Django does the aggregation,
 * the eligibility, the ordering and the pagination.
 */
export function fetchStandings(
  query: StandingsQuery,
  signal?: AbortSignal
): Promise<StandingsResponse> {
  return getJson<StandingsResponse>(
    `/contest-board/${query.contestId}/standings/`,
    standingsParams(query),
    signal
  );
}

/**
 * The rows behind one number.
 *
 * No dates are sent. The server resolves the period from the tier — a
 * browser-supplied window is not authoritative, and a client that could choose one
 * could show a number the standings cell never claimed.
 */
export function fetchProof(
  input: {
    contestId: number;
    tierId: number;
    agentId: number;
    metric: ThresholdMetric;
    cursor?: string;
  },
  signal?: AbortSignal
): Promise<ProofResponse> {
  const params = new URLSearchParams({
    agent_id: String(input.agentId),
    tier_id: String(input.tierId),
    metric: input.metric,
  });
  if (input.cursor) params.set('cursor', input.cursor);
  return getJson<ProofResponse>(`/contest-board/${input.contestId}/proof/`, params, signal);
}

/** The privacy-approved agent profile and its cycle-safe paths. */
export function fetchAgentProfile(
  input: { contestId: number; agentId: number },
  signal?: AbortSignal
): Promise<ProfileResponse> {
  return getJson<ProfileResponse>(
    `/contest-board/${input.contestId}/agents/${input.agentId}/`,
    new URLSearchParams(),
    signal
  );
}

/** The featured-contest card: the top ten closest to qualifying and top qualifiers. */
export function fetchShowcase(contestId: number, signal?: AbortSignal): Promise<ShowcaseResponse> {
  return getJson<ShowcaseResponse>(
    `/contest-board/${contestId}/showcase/`,
    new URLSearchParams(),
    signal
  );
}

/** A short-lived storage URL for the flyer, when visibility policy permits. */
export function fetchFlyer(contestId: number, signal?: AbortSignal): Promise<FlyerResponse> {
  return getJson<FlyerResponse>(
    `/contest-board/${contestId}/flyer/`,
    new URLSearchParams(),
    signal
  );
}

/* --- settings (wbreporting:manage) ---------------------------------------- */

/**
 * Send a JSON body and surface the backend's stable code on failure.
 *
 * `edit_conflict` (409) is the one a caller must handle rather than merely report:
 * it means somebody else saved while this editor was open, so the right response is
 * to reload, not to retry.
 */
async function sendJson<T>(path: string, method: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${BASE}${path}`, {
    method,
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await describeFailure(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Levels, metrics, statuses, period modes and flyer limits — all host facts. */
export function fetchEditorOptions(signal?: AbortSignal): Promise<EditorOptions> {
  return getJson<EditorOptions>('/contest-settings/', new URLSearchParams(), signal);
}

/**
 * Every editable contest, with the `revision` tokens the editor needs.
 *
 * Deliberately not Package 1's `contests/` CRUD: that serializer carries no
 * `revision`, so a save built from it could never satisfy the concurrency check.
 */
export async function fetchEditableContests(signal?: AbortSignal): Promise<EditableContest[]> {
  const body = await getJson<{ results: EditableContest[] }>(
    '/contest-settings/contests/',
    new URLSearchParams(),
    signal
  );
  return body.results;
}

/** Create a contest. Tiers are added afterwards, once a revision exists. */
export function createContest(input: {
  contest_name: string;
  contest_status: string;
  period_mode: string;
  qualifying_start?: string | null;
  qualifying_end?: string | null;
  rolling_days?: number | null;
  notes?: string;
}): Promise<EditableContest> {
  return sendJson<EditableContest>('/contest-settings/contests/', 'POST', input);
}

/**
 * Save a contest and its tiers in one validated transaction.
 *
 * `replace_tiers` must be explicit for an omitted tier to be deleted — the backend
 * will not infer a full replacement from the payload's shape, so a partial save
 * cannot destroy a tier it simply did not send.
 */
export function saveContest(
  contestId: number,
  body: Record<string, unknown>
): Promise<EditableContest> {
  return sendJson<EditableContest>(`/contest-settings/${contestId}/`, 'PUT', body);
}

/** Hide or unhide a contest. Hiding preserves it; deleting does not. */
export function setContestHidden(
  contestId: number,
  input: { hidden: boolean; revision: number }
): Promise<EditableContest> {
  return sendJson<EditableContest>(
    `/contest-settings/${contestId}/visibility/`,
    'PATCH',
    input
  );
}

/** Publish or unpublish an already-uploaded flyer. */
export function setFlyerVisible(
  contestId: number,
  input: { visible: boolean; revision: number }
): Promise<EditableContest> {
  return sendJson<EditableContest>(
    `/contest-settings/${contestId}/flyer-visibility/`,
    'PATCH',
    input
  );
}

/** Soft-delete a contest. The row survives; it leaves every list. */
export function deleteContest(contestId: number, revision: number): Promise<void> {
  return sendJson<void>(`/contest-settings/${contestId}/delete/`, 'DELETE', { revision });
}

/**
 * Upload a flyer.
 *
 * No `Content-Type` header: the browser must set the multipart boundary itself, and
 * overriding it with `application/json` is the classic way to make an upload fail
 * with an unhelpful parser error.
 */
export async function uploadFlyer(
  contestId: number,
  file: File,
  revision: number
): Promise<EditableContest> {
  const form = new FormData();
  form.append('file', file);
  form.append('revision', String(revision));
  const token = localStorage.getItem('wb.authToken');
  const response = await fetch(
    `${API_BASE_URL}${BASE}/contest-settings/${contestId}/flyer/`,
    { method: 'POST', headers: { Authorization: `Token ${token}` }, body: form }
  );
  if (!response.ok) throw await describeFailure(response);
  return (await response.json()) as EditableContest;
}

/** Remove a flyer; the stored file is discarded once the metadata write commits. */
export function removeFlyer(contestId: number, revision: number): Promise<EditableContest> {
  return sendJson<EditableContest>(`/contest-settings/${contestId}/flyer/`, 'DELETE', {
    revision,
  });
}
